import { useInfiniteQuery } from "@tanstack/react-query";
import { apiQueryAdapter } from "app/data/apiQueryAdapter";
import { PageResponseModel } from "app/data/common/models";
import { QUERY_KEYS } from "app/data/common/queryKeys";
import { CloneManifestChangeLogProjection } from "app/data/static-load/model";

export interface ActivityLogFilters {
  action?: string;
  search?: string;
  dateFrom?: string;
  dateTo?: string;
}

const PAGE_SIZE = 50;

export const useActivityLog = (filters: ActivityLogFilters = {}, enabled = true) => {
  const { action, search, dateFrom, dateTo } = filters;

  const {
    data,
    fetchNextPage,
    hasNextPage,
    isLoading,
    isFetchingNextPage,
    isFetching,
    isError,
    refetch,
  } = useInfiniteQuery({
    queryKey: [QUERY_KEYS.GET_ACTIVITY_LOG, action, search, dateFrom, dateTo],
    queryFn: async ({ pageParam = 0 }) => {
      const params: Record<string, string | number> = {
        page: pageParam,
        size: PAGE_SIZE,
      };

      if (action) params.action = action;
      if (search) params.search = search;
      if (dateFrom) params.dateFrom = dateFrom;
      if (dateTo) params.dateTo = dateTo;

      const response = await apiQueryAdapter.query<PageResponseModel<CloneManifestChangeLogProjection>>(
        "/manifest-management/activity-log/all",
        "GET",
        { params }
      );

      return response.data;
    },
    getNextPageParam: (lastPage) => {
      if (lastPage?.last) return undefined;
      return lastPage?.pageable?.pageNumber + 1;
    },
    enabled,
    keepPreviousData: false,
    refetchOnWindowFocus: false,
    retry: false,
  });

  return {
    data,
    fetchNextPage,
    hasNextPage,
    isLoading,
    isFetchingNextPage,
    isFetching,
    isError,
    refetch,
  };
};
